import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../booking/api";
import { BookShell } from "../booking/BookShell";
import { BookingSummary } from "../booking/BookingSummary";
import { downloadIcs } from "../booking/utils";
import { useBT } from "../booking/i18n";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function ManageBooking() {
  const { t } = useBT();
  const { token } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [booking, setBooking] = useState(undefined);
  const [confirming, setConfirming] = useState(!!sessionId);

  const fetchBooking = () => api.get(`/api/public/bookings/${token}`).then((r) => setBooking(r.data)).catch(() => setBooking(null));

  useEffect(() => { fetchBooking(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const r = await api.get(`/api/payments/status/${sessionId}`);
        if (r.data.payment_status !== "pending" || tries >= 15) {
          setConfirming(false);
          fetchBooking();
          return;
        }
      } catch {
        setConfirming(false);
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = async () => {
    try {
      const r = await api.post(`/api/public/bookings/${token}/cancel`);
      setBooking(r.data);
      toast.success(t.cancelledToast);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const cancelled = booking?.status === "Cancelled";
  const awaiting = !cancelled && booking?.payment_status === "pending";
  const upcoming = booking && new Date(`${booking.start_utc}Z`) > new Date();
  const refundable = booking && new Date(`${booking.start_utc}Z`) - new Date() >= 24 * 3600 * 1000;

  return (
    <BookShell>
      {booking === undefined && <p className="text-sm text-[#1d2b31]/50">{t.loading}</p>}
      {confirming && (
        <p className="flex items-center gap-2 text-sm mb-6" data-testid="manage-payment-confirming">
          <Loader2 size={15} className="animate-spin text-[#b09873]" /> {t.paying}
        </p>
      )}
      {params.get("payment") === "cancelled" && booking?.payment_status === "pending" && (
        <p className="text-sm mb-6 bg-amber-50 border border-amber-200 px-4 py-3 rounded" data-testid="manage-payment-cancelled">
          {t.payCancelled}
        </p>
      )}
      {booking === null && <p data-testid="manage-not-found">{t.notFound}</p>}
      {booking && (
        <section className="bg-white border rounded-xl p-8 max-w-xl" data-testid="manage-booking">
          <p className="text-xs uppercase tracking-[0.25em] text-[#8a7347]">{t.yourRes}</p>
          <h1 className="text-2xl tracking-wider mt-2 font-mono" data-testid="manage-code">{booking.code}</h1>
          <span data-testid="manage-status"
            className={`inline-block mt-3 text-[11px] uppercase tracking-widest px-3 py-1 rounded-full ${
              cancelled ? "bg-stone-200 text-stone-600" : awaiting ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
            {awaiting ? t.pay.pending : t.statuses[booking.status] || booking.status}
          </span>
          <div className="mt-6"><BookingSummary booking={booking} /></div>
          {!cancelled && upcoming && (
            <div className="flex flex-wrap gap-2 mt-8">
              {awaiting && booking.checkout_url ? (
                <a href={booking.checkout_url} data-testid="manage-resume-payment"
                  className="rounded-full px-4 py-2 text-xs bg-[#15262d] text-white hover:bg-[#b09873] transition-colors">{t.payResume}</a>
              ) : (
                <button onClick={() => downloadIcs(booking)} data-testid="manage-add-calendar"
                  className="border rounded-full px-4 py-2 text-xs hover:border-[#15262d] transition-colors">{t.addCal}</button>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid="manage-cancel-btn" className="rounded-full px-4 py-2 text-xs bg-red-50 text-red-700 hover:bg-red-100 transition-colors">
                    {t.cancelBtn}
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t.cancelQ}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t.cancelDesc}
                      {booking.payment_status === "paid" && (
                        <span className="block mt-2 font-medium" data-testid="manage-refund-policy">
                          {refundable ? t.refundYes : t.refundNo}
                        </span>
                      )}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="manage-cancel-dismiss">{t.keep}</AlertDialogCancel>
                    <AlertDialogAction onClick={cancel} data-testid="manage-cancel-confirm">{t.yesCancel}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </section>
      )}
    </BookShell>
  );
}
